/**
 * Billing Helper Functions
 *
 * Utility functions for billing operations. All types are now imported from proto.
 */

import {
  InvoiceType,
  InvoiceStatus,
  PaymentStatus,
  PartyType,
  SupplyType,
  ResetFrequency,
  GSTRegistrationType,
  type InvoiceItem,
} from '@/gen/proto';

import { getCurrencyConfig, toSmallestUnit, fromSmallestUnit } from './formatters';

// ============================================================================
// Amount Formatting
// ============================================================================

/**
 * Format amount in smallest currency unit to display string.
 * Uses currency config for correct decimal places and locale.
 */
export function formatAmount(
  amount: number,
  currency: string
): string {
  if (!currency?.trim()) {return amount.toLocaleString()}
  const config = getCurrencyConfig(currency);
  const decimal = fromSmallestUnit(amount, currency);

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: config.decimalPlaces,
    maximumFractionDigits: config.decimalPlaces,
  }).format(decimal);
}

/**
 * Convert decimal amount to smallest currency unit.
 */
export function toPaisa(amount: number, currency: string = 'INR'): number {
  return toSmallestUnit(amount, currency);
}

/**
 * Convert smallest currency unit to decimal amount.
 */
export function toRupees(amount: number, currency: string = 'INR'): number {
  return fromSmallestUnit(amount, currency);
}

// ============================================================================
// GST Calculations
// ============================================================================

/**
 * Calculate GST amounts based on supply type.
 */
export function calculateGST(
  taxableAmount: number,
  gstRate: number,
  supplyType: SupplyType
): { cgst: number; sgst: number; igst: number } {
  if (supplyType === SupplyType.SUPPLY_TYPE_EXPORT) {
    return { cgst: 0, sgst: 0, igst: 0 };
  }

  if (supplyType === SupplyType.SUPPLY_TYPE_INTERSTATE || supplyType === SupplyType.SUPPLY_TYPE_SEZ) {
    return {
      cgst: 0,
      sgst: 0,
      igst: Math.round(taxableAmount * gstRate / 100),
    };
  }

  // Intrastate
  const halfRate = gstRate / 2;
  return {
    cgst: Math.round(taxableAmount * halfRate / 100),
    sgst: Math.round(taxableAmount * halfRate / 100),
    igst: 0,
  };
}

/**
 * Determine supply type based on seller and buyer state codes.
 */
export function determineSupplyType(
  sellerStateCode: string,
  buyerStateCode: string | undefined,
  isExport: boolean
): SupplyType {
  if (isExport || !buyerStateCode) {
    return SupplyType.SUPPLY_TYPE_EXPORT;
  }
  return sellerStateCode === buyerStateCode ? SupplyType.SUPPLY_TYPE_INTRASTATE : SupplyType.SUPPLY_TYPE_INTERSTATE;
}

// ============================================================================
// Display Labels
// ============================================================================

/**
 * Get invoice type display name.
 */
export function getInvoiceTypeLabel(type: InvoiceType): string {
  const labels: Partial<Record<InvoiceType, string>> = {
    [InvoiceType.INVOICE_TYPE_UNSPECIFIED]: 'Unspecified',
    [InvoiceType.INVOICE_TYPE_B2B]: 'B2B Tax Invoice',
    [InvoiceType.INVOICE_TYPE_EXPORT]: 'Export Invoice',
    [InvoiceType.INVOICE_TYPE_B2C]: 'B2C Invoice',
    [InvoiceType.INVOICE_TYPE_CREDIT_NOTE]: 'Credit Note',
    [InvoiceType.INVOICE_TYPE_DEBIT_NOTE]: 'Debit Note',
    [InvoiceType.INVOICE_TYPE_VENDOR_BILL]: 'Vendor Bill',
    [InvoiceType.INVOICE_TYPE_VENDOR_CREDIT_NOTE]: 'Vendor Credit Note',
  };
  return labels[type] ?? 'Unknown';
}

/**
 * Get invoice status display name.
 */
export function getInvoiceStatusLabel(status: InvoiceStatus): string {
  const labels: Partial<Record<InvoiceStatus, string>> = {
    [InvoiceStatus.INVOICE_STATUS_UNSPECIFIED]: 'Unspecified',
    [InvoiceStatus.INVOICE_STATUS_DRAFT]: 'Draft',
    [InvoiceStatus.INVOICE_STATUS_POSTED]: 'Posted',
    [InvoiceStatus.INVOICE_STATUS_VOIDED]: 'Voided',
    [InvoiceStatus.INVOICE_STATUS_CANCELLED]: 'Cancelled',
  };
  return labels[status] ?? 'Unknown';
}

/**
 * Get payment status display name.
 */
export function getPaymentStatusLabel(status: PaymentStatus): string {
  const labels: Partial<Record<PaymentStatus, string>> = {
    [PaymentStatus.PAYMENT_STATUS_UNSPECIFIED]: 'Unspecified',
    [PaymentStatus.PAYMENT_STATUS_UNPAID]: 'Unpaid',
    [PaymentStatus.PAYMENT_STATUS_PARTIAL]: 'Partially Paid',
    [PaymentStatus.PAYMENT_STATUS_PAID]: 'Paid',
    [PaymentStatus.PAYMENT_STATUS_OVERPAID]: 'Overpaid',
    [PaymentStatus.PAYMENT_STATUS_WRITTEN_OFF]: 'Written Off',
  };
  return labels[status] ?? 'Unknown';
}

/**
 * Get due status display label.
 */
export function getDueStatusLabel(status: string): string {
  switch (status) {
    case 'pending': return 'Not Due';
    case 'due': return 'Due';
    case 'overdue': return 'Overdue';
    default: return '';
  }
}

/**
 * Get badge variant for due status.
 */
export function getDueStatusBadgeVariant(status: string): 'secondary' | 'warning' | 'error' {
  switch (status) {
    case 'due': return 'warning';
    case 'overdue': return 'error';
    default: return 'secondary';
  }
}

/**
 * Get aging bucket label based on days overdue and bucket boundaries.
 */
export function getAgingBucketLabel(daysOverdue: number, buckets: number[] = [30, 60, 90, 120]): string {
  if (daysOverdue <= 0) {return 'Current';}
  for (let i = 0; i < buckets.length; i++) {
    /* v8 ignore next 2 */
    const lower = i === 0 ? 1 : (buckets[i - 1] ?? 0) + 1;
    if (daysOverdue <= (buckets[i] ?? 0)) {
      return `${lower}-${buckets[i]} days`;
    }
  }
  return `${buckets[buckets.length - 1]}+ days`;
}

/**
 * Normalize payment status from proto enum format or lowercase.
 * "PAYMENT_STATUS_WRITTEN_OFF" → "written_off", "written_off" → "written_off"
 */
export function normalizePaymentStatus(ps: string | undefined): string {
  if (!ps) {return 'unpaid'}
  return ps.toLowerCase().replace('payment_status_', '')
}

/**
 * Get badge variant for a normalized payment status string.
 */
export function getPaymentStatusBadgeVariant(ps: string): 'secondary' | 'warning' | 'success' | 'error' {
  if (ps === 'partial') {return 'warning'}
  if (ps === 'paid') {return 'success'}
  if (ps === 'overpaid' || ps === 'written_off') {return 'error'}
  return 'secondary'
}

/**
 * Get display label for a normalized payment status string.
 */
export function getPaymentStatusDisplayLabel(ps: string): string {
  const m: Record<string, string> = {
    unpaid: 'Unpaid', partial: 'Partial', paid: 'Paid',
    overpaid: 'Overpaid', written_off: 'Written Off',
  }
  return m[ps] ?? ps
}

/**
 * Get party type display name.
 */
export function getPartyTypeLabel(type: PartyType): string {
  const labels: Partial<Record<PartyType, string>> = {
    [PartyType.PARTY_TYPE_UNSPECIFIED]: 'Unspecified',
    [PartyType.PARTY_TYPE_CUSTOMER]: 'Customer',
    [PartyType.PARTY_TYPE_VENDOR]: 'Vendor',
    [PartyType.PARTY_TYPE_BOTH]: 'Customer & Vendor',
  };
  return labels[type] ?? 'Unknown';
}

// ============================================================================
// Enum Conversions
// ============================================================================

/**
 * Convert string to PartyType enum
 */
export function stringToPartyType(value: string): PartyType {
  switch (value.toLowerCase()) {
    case 'customer':
      return PartyType.PARTY_TYPE_CUSTOMER;
    case 'vendor':
      return PartyType.PARTY_TYPE_VENDOR;
    case 'both':
      return PartyType.PARTY_TYPE_BOTH;
    default:
      return PartyType.PARTY_TYPE_UNSPECIFIED;
  }
}

/**
 * Convert PartyType enum to string
 */
export function partyTypeToString(type: PartyType): 'customer' | 'vendor' | 'both' | '' {
  switch (type) {
    case PartyType.PARTY_TYPE_CUSTOMER:
      return 'customer';
    case PartyType.PARTY_TYPE_VENDOR:
      return 'vendor';
    case PartyType.PARTY_TYPE_BOTH:
      return 'both';
    default:
      return '';
  }
}

/**
 * Convert roles array to the deprecated PartyType enum (for backward compat).
 */
export function rolesToPartyType(roles: string[]): PartyType {
  const hasCustomer = roles.includes('customer')
  const hasSupplier = roles.includes('supplier')
  if (hasCustomer && hasSupplier) { return PartyType.PARTY_TYPE_BOTH }
  if (hasCustomer) { return PartyType.PARTY_TYPE_CUSTOMER }
  if (hasSupplier) { return PartyType.PARTY_TYPE_VENDOR }
  return PartyType.PARTY_TYPE_UNSPECIFIED
}

/**
 * Convert PartyType enum to roles array.
 */
export function partyTypeToRoles(pt: PartyType): string[] {
  switch (pt) {
    case PartyType.PARTY_TYPE_CUSTOMER: return ['customer']
    case PartyType.PARTY_TYPE_VENDOR: return ['supplier']
    case PartyType.PARTY_TYPE_BOTH: return ['customer', 'supplier']
    default: return []
  }
}

/**
 * Check if a party has a given role.
 */
export function hasRole(party: { roles?: string[] }, role: string): boolean {
  return party.roles?.includes(role) ?? false
}

/**
 * Check if an address is used for a specific purpose (billing, shipping, consignee).
 * Backward-compatible: checks the new `usage` array first, then falls back to
 * legacy `type` and `label` fields for addresses that haven't been migrated yet.
 */
export function addressHasUsage(
  addr: { usage?: string[]; type?: string; label?: string },
  use: string,
): boolean {
  if (addr.usage && addr.usage.length > 0) {
    return addr.usage.includes(use)
  }
  // Legacy fallback: old addresses stored usage in type or label
  return addr.type === use || addr.label === use
}

/**
 * Convert string to GSTRegistrationType enum
 */
export function stringToGSTRegistrationType(value: string): GSTRegistrationType {
  switch (value.toLowerCase()) {
    case 'regular':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_REGULAR;
    case 'composition':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_COMPOSITION;
    case 'unregistered':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_UNREGISTERED;
    case 'consumer':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_CONSUMER;
    case 'overseas':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_OVERSEAS;
    case 'sez':
      return GSTRegistrationType.GST_REGISTRATION_TYPE_SEZ;
    default:
      return GSTRegistrationType.GST_REGISTRATION_TYPE_UNSPECIFIED;
  }
}

/**
 * Convert GSTRegistrationType enum to string
 */
export function gstRegistrationTypeToString(type: GSTRegistrationType): string {
  switch (type) {
    case GSTRegistrationType.GST_REGISTRATION_TYPE_REGULAR:
      return 'regular';
    case GSTRegistrationType.GST_REGISTRATION_TYPE_COMPOSITION:
      return 'composition';
    case GSTRegistrationType.GST_REGISTRATION_TYPE_UNREGISTERED:
      return 'unregistered';
    case GSTRegistrationType.GST_REGISTRATION_TYPE_CONSUMER:
      return 'consumer';
    case GSTRegistrationType.GST_REGISTRATION_TYPE_OVERSEAS:
      return 'overseas';
    case GSTRegistrationType.GST_REGISTRATION_TYPE_SEZ:
      return 'sez';
    default:
      return '';
  }
}

/**
 * Convert string to InvoiceType enum
 */
export function stringToInvoiceType(value: string): InvoiceType {
  switch (value.toLowerCase()) {
    case 'b2b':
      return InvoiceType.INVOICE_TYPE_B2B;
    case 'export':
      return InvoiceType.INVOICE_TYPE_EXPORT;
    case 'b2c':
      return InvoiceType.INVOICE_TYPE_B2C;
    case 'credit_note':
      return InvoiceType.INVOICE_TYPE_CREDIT_NOTE;
    case 'debit_note':
      return InvoiceType.INVOICE_TYPE_DEBIT_NOTE;
    default:
      return InvoiceType.INVOICE_TYPE_UNSPECIFIED;
  }
}

/**
 * Convert InvoiceType enum to string
 */
export function invoiceTypeToString(type: InvoiceType): string {
  switch (type) {
    case InvoiceType.INVOICE_TYPE_B2B:
      return 'b2b';
    case InvoiceType.INVOICE_TYPE_EXPORT:
      return 'export';
    case InvoiceType.INVOICE_TYPE_B2C:
      return 'b2c';
    case InvoiceType.INVOICE_TYPE_CREDIT_NOTE:
      return 'credit_note';
    case InvoiceType.INVOICE_TYPE_DEBIT_NOTE:
      return 'debit_note';
    default:
      return '';
  }
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate GSTIN format.
 */
export function isValidGSTIN(gstin: string): boolean {
  if (!gstin) { return true; } // Empty is valid (for optional fields)
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstinRegex.test(gstin);
}

/**
 * Extract state code from GSTIN.
 */
export function getStateCodeFromGSTIN(gstin: string): string | undefined {
  if (!gstin || gstin.length < 2) { return undefined; }
  return gstin.substring(0, 2);
}

// ============================================================================
// Invoice Calculations
// ============================================================================

/**
 * Invoice totals interface
 */
export interface InvoiceTotals {
  subtotal: number;
  discountAmount: number;
  taxableAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessAmount: number;
  totalTax: number;
  totalAmount: number;
}

/**
 * Calculate invoice totals from items.
 */
export function calculateInvoiceTotals(items: InvoiceItem[]): InvoiceTotals {
  return items.reduce(
    (totals, item) => ({
      subtotal: totals.subtotal + Number(item.grossAmount ?? 0),
      discountAmount: totals.discountAmount + Number(item.discountAmount ?? 0),
      taxableAmount: totals.taxableAmount + Number(item.taxableAmount ?? 0),
      cgstAmount: totals.cgstAmount + Number(item.cgstAmount ?? 0),
      sgstAmount: totals.sgstAmount + Number(item.sgstAmount ?? 0),
      igstAmount: totals.igstAmount + Number(item.igstAmount ?? 0),
      cessAmount: totals.cessAmount + Number(item.cessAmount ?? 0),
      totalTax: totals.totalTax + Number(item.totalTax ?? 0),
      totalAmount: totals.totalAmount + Number(item.totalAmount ?? 0),
    }),
    {
      subtotal: 0,
      discountAmount: 0,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cessAmount: 0,
      totalTax: 0,
      totalAmount: 0,
    }
  );
}

// ============================================================================
// Document Numbering Utilities
// ============================================================================

/**
 * Get reset frequency display label.
 */
export function getResetFrequencyLabel(frequency: ResetFrequency): string {
  const labels: Partial<Record<ResetFrequency, string>> = {
    [ResetFrequency.RESET_FREQUENCY_UNSPECIFIED]: 'Unspecified',
    [ResetFrequency.RESET_FREQUENCY_NEVER]: 'Never',
    [ResetFrequency.RESET_FREQUENCY_MONTHLY]: 'Monthly',
    [ResetFrequency.RESET_FREQUENCY_QUARTERLY]: 'Quarterly',
    [ResetFrequency.RESET_FREQUENCY_YEARLY]: 'Yearly',
  };
  return labels[frequency] ?? 'Unknown';
}

/**
 * Get series type display label (Draft or Final).
 */
export function getSeriesTypeLabel(isDraft: boolean): string {
  return isDraft ? 'Draft (Proforma)' : 'Final';
}

// ============================================================================
// GST Rates
// ============================================================================

export const GST_RATES = {
  // Common GST rates in India (in %)
  EXEMPT: 0,
  RATE_0_25: 0.25,
  RATE_3: 3,
  RATE_5: 5,
  RATE_12: 12,
  RATE_18: 18,
  RATE_28: 28,
} as const;
